<template>
  <div class="telegram-layout">
    <Sidebar />

    <div class="main-content">
      <TopHeader />

      <main class="page-content">
      <div class="telegram-container">

      <!-- ── LEFT: Inbox panel ────────────────────────────────────────── -->
      <div class="inbox-panel" :class="{ 'hidden-mobile': selectedContact }">

        <!-- Inbox header -->
        <div class="inbox-header">
          <div class="inbox-header-top">
            <div class="inbox-title-row">
              <div class="channel-icon">
                <img :src="telegramLogo" alt="Telegram" width="28" height="28" style="border-radius:8px;display:block;" />
              </div>
              <h2 class="inbox-title">Telegram</h2>
              <span v-if="unreadCount > 0" class="unread-badge">{{ unreadCount }}</span>
            </div>
            <div class="inbox-controls">
              <button
                class="control-btn"
                :class="{ active: showArchived }"
                @click="toggleArchived"
                title="Archived"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- Bot selector -->
          <div v-if="bots.length > 1" class="bot-selector">
            <select v-model="selectedBotId" @change="loadInbox" class="bot-select">
              <option value="">All Bots</option>
              <option v-for="bot in bots" :key="bot.id" :value="bot.id">
                @{{ bot.bot_username }}
              </option>
            </select>
          </div>

          <!-- Search -->
          <div class="search-box">
            <svg class="search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              v-model="searchQuery"
              @input="onSearch"
              type="text"
              placeholder="Search contacts..."
              class="search-input"
            />
          </div>
        </div>

        <!-- Contact list -->
        <div class="contacts-list" ref="contactsListRef">
          <div v-if="loadingInbox" class="list-loading">
            <div class="spinner"></div>
          </div>

          <div
            v-else-if="contacts.length === 0"
            class="list-empty"
          >
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="color:#bbb">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            <p>{{ showArchived ? 'No archived conversations' : 'No conversations yet' }}</p>
          </div>

          <div
            v-else
            v-for="contact in contacts"
            :key="`${contact.telegram_user_id}_${contact.channel_id}`"
            class="contact-item"
            :class="{ active: isSelected(contact), unread: !contact.case_resolved }"
            @click="selectContact(contact)"
          >
            <div class="contact-avatar" :style="{ background: avatarColor(contact) }">
              <img v-if="contact.profile_pic" :src="contact.profile_pic" :alt="contactName(contact)" class="avatar-img" />
              <span v-else class="avatar-initials">{{ initials(contact) }}</span>
            </div>
            <div class="contact-info">
              <div class="contact-top">
                <span class="contact-name">{{ contactName(contact) }}</span>
                <span class="contact-time">{{ formatTime(contact.last_message_time || contact.last_message_at) }}</span>
              </div>
              <div class="contact-preview">
                <span class="preview-text">
                  <span v-if="contact.last_role === 'Agent'" class="you-prefix">You: </span>
                  <span v-if="contact.last_attachment_type && !contact.last_message">
                    {{ attachmentLabel(contact.last_attachment_type) }}
                  </span>
                  <span v-else>{{ contact.last_message || '...' }}</span>
                </span>
              </div>
              <div v-if="bots.length > 1" class="contact-bot">@{{ contact.bot_username }}</div>
            </div>
            <div v-if="!contact.case_resolved" class="unread-dot"></div>
          </div>
        </div>
      </div>

      <!-- ── RIGHT: Conversation panel ───────────────────────────────── -->
      <div class="chat-panel" :class="{ 'hidden-mobile': !selectedContact }">

        <!-- No contact selected -->
        <div v-if="!selectedContact" class="chat-empty">
          <div class="chat-empty-icon">
            <img :src="telegramLogo" alt="Telegram" width="72" height="72" style="border-radius:50%;opacity:.35" />
          </div>
          <p>Select a conversation to start chatting</p>
        </div>

        <template v-else>

          <!-- Chat header -->
          <div class="chat-header">
            <button class="back-btn" @click="selectedContact = null">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
            </button>
            <div class="chat-header-avatar" :style="{ background: avatarColor(selectedContact) }">
              <img v-if="selectedContact.profile_pic" :src="selectedContact.profile_pic" class="avatar-img" />
              <span v-else class="avatar-initials">{{ initials(selectedContact) }}</span>
            </div>
            <div class="chat-header-info">
              <div class="chat-header-name">{{ contactName(selectedContact) }}</div>
              <div class="chat-header-sub">
                <span v-if="selectedContact.username">@{{ selectedContact.username }} · </span>
                <span v-if="selectedContact.phone_number">{{ selectedContact.phone_number }} · </span>
                Telegram
              </div>
            </div>
            <div class="chat-header-actions">
              <button
                v-if="!selectedContact.phone_number"
                class="action-btn action-btn-phone"
                :disabled="requestingPhone"
                @click="requestPhoneNumber"
                title="Request phone number from customer"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.95 9.61a19.79 19.79 0 01-3.07-8.68A2 2 0 012.86 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L7.09 8.68a16 16 0 006.29 6.29l1.05-1.06a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                </svg>
                {{ requestingPhone ? 'Sending…' : 'Request Phone' }}
              </button>
              <button
                class="action-btn"
                :class="{ 'action-resolved': selectedContact.case_resolved }"
                @click="toggleResolved"
                :title="selectedContact.case_resolved ? 'Mark as Unresolved' : 'Mark as Resolved'"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                {{ selectedContact.case_resolved ? 'Reopen' : 'Resolve' }}
              </button>
              <button
                class="action-btn"
                @click="toggleArchive"
                :title="selectedContact.is_archived ? 'Unarchive' : 'Archive'"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/>
                </svg>
                {{ selectedContact.is_archived ? 'Unarchive' : 'Archive' }}
              </button>
              <button
                class="action-btn"
                :class="{ 'action-blocked': selectedContact.is_blocked }"
                @click="toggleBlock"
                :title="selectedContact.is_blocked ? 'Unblock contact' : 'Block contact'"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                </svg>
                {{ selectedContact.is_blocked ? 'Unblock' : 'Block' }}
              </button>
            </div>
          </div>

          <!-- Notes panel -->
          <div class="notes-panel">
            <button class="notes-toggle" @click="showNotesPanel = !showNotesPanel">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/>
              </svg>
              Notes
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" :style="{ transform: showNotesPanel ? 'rotate(180deg)' : '', transition: 'transform .2s' }">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
            <div v-if="showNotesPanel" class="notes-body">
              <textarea
                v-model="selectedContact.notes"
                @blur="saveNotes"
                placeholder="Private notes about this contact (not visible to customer)…"
                class="notes-textarea"
                rows="3"
              ></textarea>
              <span v-if="savingNotes" class="notes-saving">Saving…</span>
            </div>
          </div>

          <!-- Messages area -->
          <div class="messages-area" ref="messagesAreaRef" @scroll="onMessagesScroll">
            <div v-if="loadingMessages" class="messages-loading">
              <div class="spinner"></div>
            </div>

            <template v-else>
              <!-- Top-of-history indicators (sit above the message list) -->
              <div v-if="loadingMoreMessages" class="load-more-row">
                <div class="spinner spinner-sm"></div>
              </div>
              <div v-else-if="!hasMoreMessages && messages.length > 0" class="load-more-row load-more-end">
                Start of conversation
              </div>

              <div v-if="selectedContact.is_blocked" class="blocked-banner">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
                This contact is blocked — new messages are silently dropped
              </div>

              <div
                v-for="msg in messages"
                :key="msg.id"
                class="message-row"
                :class="msg.conversation_role === 'Agent' ? 'row-agent' : 'row-customer'"
                @mouseenter="hoveredMsgId = msg.id"
                @mouseleave="hoveredMsgId = null"
              >
                <!-- Reply context (sits above the bubble row) -->
                <div v-if="msg.reply_to_snapshot" class="reply-context">
                  <div class="reply-bar"></div>
                  <div class="reply-content">
                    <span class="reply-sender">{{ msg.reply_to_snapshot.sender || 'Message' }}</span>
                    <span class="reply-text">{{ msg.reply_to_snapshot.text || '[Media]' }}</span>
                  </div>
                </div>

                <!-- Body row: actions beside bubble ────────────── -->
                <div class="msg-body">

                <!-- Left actions — shown for agent messages -->
                <div
                  v-if="msg.conversation_role === 'Agent'"
                  class="msg-actions"
                  :style="{ opacity: hoveredMsgId === msg.id ? 1 : 0, pointerEvents: hoveredMsgId === msg.id ? 'auto' : 'none' }"
                >
                  <button class="msg-action-btn" title="Reply" @click.stop="startReply(msg)">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 0 0-4-4H4"/>
                    </svg>
                  </button>
                  <button class="msg-action-btn" title="Forward" @click.stop="startForward(msg)">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="15 17 20 12 15 7"/><path d="M4 18v-2a4 4 0 0 1 4-4h12"/>
                    </svg>
                  </button>
                </div>

                <!-- Bubble -->
                <div class="message-bubble" :class="msg.conversation_role === 'Agent' ? 'bubble-agent' : 'bubble-customer'">

                  <!-- Image -->
                  <template v-if="msg.attachment_type === 'photo' || msg.image_url">
                    <img :src="msg.image_url" class="msg-media-img" @click="openImage(msg.image_url)" />
                    <p v-if="msg.message" class="msg-caption">{{ msg.message }}</p>
                  </template>

                  <!-- Video -->
                  <template v-else-if="msg.attachment_type === 'video' || msg.video_url">
                    <video :src="msg.video_url" controls class="msg-media-video"></video>
                    <p v-if="msg.message" class="msg-caption">{{ msg.message }}</p>
                  </template>

                  <!-- Voice / Audio -->
                  <template v-else-if="msg.attachment_type === 'voice' || msg.voice_url">
                    <div class="msg-audio">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                        <path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/>
                        <line x1="8" y1="23" x2="16" y2="23"/>
                      </svg>
                      <audio :src="msg.voice_url || msg.audio_url" controls class="audio-player"></audio>
                    </div>
                  </template>

                  <!-- Document -->
                  <template v-else-if="msg.attachment_type === 'document' || msg.document_url">
                    <a :href="msg.document_url" target="_blank" class="msg-document">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14 2 14 8 20 8"/>
                      </svg>
                      {{ msg.message || 'Document' }}
                    </a>
                  </template>

                  <!-- Sticker -->
                  <template v-else-if="msg.attachment_type === 'sticker'">
                    <img :src="msg.image_url" class="msg-sticker" />
                  </template>

                  <!-- Poll / Quiz (sent by agent) -->
                  <template v-else-if="msg.attachment_type === 'poll' || msg.attachment_type === 'quiz'">
                    <div class="msg-poll">
                      <div class="msg-poll-header">
                        <span class="msg-poll-icon">{{ msg.attachment_type === 'quiz' ? '🧠' : '📊' }}</span>
                        <span class="msg-poll-label">{{ msg.attachment_type === 'quiz' ? 'Quiz' : 'Poll' }}</span>
                      </div>
                      <p class="msg-poll-question">{{ msg.message }}</p>
                      <div v-if="msg.poll_preview" class="msg-poll-options">
                        <div
                          v-for="(opt, oi) in msg.poll_preview.options"
                          :key="oi"
                          class="msg-poll-option"
                        >
                          <span :class="msg.attachment_type === 'quiz' && oi === msg.poll_preview.correctOption ? 'opt-correct' : 'opt-radio'">
                            {{ msg.attachment_type === 'quiz' && oi === msg.poll_preview.correctOption ? '✓' : '○' }}
                          </span>
                          <span>{{ opt }}</span>
                        </div>
                      </div>
                    </div>
                  </template>

                  <!-- Callback query / keyboard interaction -->
                  <template v-else-if="msg.attachment_type === 'callback_query' || msg.attachment_type === 'poll_answer'">
                    <span class="msg-interactive">{{ msg.message }}</span>
                  </template>

                  <!-- Text / location / contact / fallback -->
                  <template v-else>
                    <p class="msg-text" v-html="formatMessageText(msg.message)"></p>
                  </template>

                  <div class="msg-footer">
                    <span v-if="msg.is_edited" class="msg-edited">edited</span>
                    <span class="msg-time">{{ formatMsgTime(msg.date_time || msg.created_at) }}</span>
                    <span v-if="msg.conversation_role === 'Agent'" class="msg-status">
                      <!-- Sending: clock -->
                      <svg v-if="msg.sending" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" title="Sending…">
                        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                      </svg>
                      <!-- Failed: red circle-x -->
                      <svg v-else-if="msg.failed" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2" title="Failed to send">
                        <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                      </svg>
                      <!-- Sent: single grey check -->
                      <svg v-else-if="msg.telegram_message_id" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" title="Sent">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </span>
                  </div>
                </div>
                <!-- End .message-bubble -->

                <!-- Right actions — shown for customer messages -->
                <div
                  v-if="msg.conversation_role === 'Customer'"
                  class="msg-actions"
                  :style="{ opacity: hoveredMsgId === msg.id ? 1 : 0, pointerEvents: hoveredMsgId === msg.id ? 'auto' : 'none' }"
                >
                  <button class="msg-action-btn" title="Reply" @click.stop="startReply(msg)">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 0 0-4-4H4"/>
                    </svg>
                  </button>
                  <button class="msg-action-btn" title="Forward" @click.stop="startForward(msg)">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="15 17 20 12 15 7"/><path d="M4 18v-2a4 4 0 0 1 4-4h12"/>
                    </svg>
                  </button>
                  <button
                    v-if="msg.message"
                    class="msg-action-btn"
                    title="Add to notes"
                    @click.stop="quickAddToNotes(msg)"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/>
                    </svg>
                  </button>
                </div>
                <!-- End .msg-body -->
                </div>

              </div>
            </template>
          </div>

          <!-- Send bar -->
          <div class="send-bar">
            <div v-if="replyingTo" class="replying-to">
              <div class="replying-to-bar"></div>
              <div class="replying-to-content">
                <span class="replying-to-label">Replying to</span>
                <span class="replying-to-text">{{ replyingTo.message || '[Media]' }}</span>
              </div>
              <button @click="replyingTo = null" class="reply-cancel">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <div class="send-row">
              <!-- Attachment button -->
              <label class="attach-btn" title="Send photo/document">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                </svg>
                <input type="file" class="hidden-file" @change="onFileSelected" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar" />
              </label>

              <!-- Quick replies -->
              <div class="quick-replies-wrapper">
                <button class="attach-btn" :class="{ active: showQuickRepliesDropdown }" @click.stop="toggleQuickReplies" title="Quick Replies">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                  </svg>
                  <span v-if="quickReplies.length" class="qr-badge">{{ quickReplies.length }}</span>
                </button>
                <div v-if="showQuickRepliesDropdown" class="qr-dropdown">
                  <div v-if="loadingQuickReplies" class="qr-loading">
                    <div class="spinner spinner-sm"></div>
                  </div>
                  <div v-else-if="!quickReplies.length" class="qr-empty">
                    No quick replies yet.
                    <router-link to="/quick-replies" class="qr-create-link">Create one</router-link>
                  </div>
                  <div v-else class="qr-list">
                    <div
                      v-for="reply in quickReplies"
                      :key="reply.id"
                      class="qr-item"
                      @click="useQuickReply(reply)"
                    >
                      <img v-if="reply.image_url" :src="reply.image_url" class="qr-thumb" />
                      <div class="qr-text">
                        <div class="qr-title">{{ reply.title }}</div>
                        <div class="qr-preview">{{ (reply.message || '📷 Image').substring(0, 55) }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Interactive Message builder toggle -->
              <button
                class="attach-btn interactive-toggle-btn"
                :class="{ active: showInteractiveBuilder }"
                @click="openInteractiveBuilder"
                title="Interactive Message (buttons, polls, quiz)"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                  <line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
                </svg>
              </button>

              <textarea
                v-model="sendText"
                @keydown.enter.exact.prevent="sendMessage"
                @keydown.enter.shift.exact="sendText += '\n'"
                placeholder="Type a message…"
                class="send-input"
                rows="1"
                ref="sendInputRef"
              ></textarea>

              <button
                @click="sendMessage"
                class="send-btn"
                :disabled="sending || (!sendText.trim() && !selectedFile)"
              >
                <svg v-if="!sending" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
                </svg>
                <div v-else class="spinner-sm"></div>
              </button>
            </div>

            <!-- File preview -->
            <div v-if="selectedFile" class="file-preview">
              <img v-if="filePreviewUrl" :src="filePreviewUrl" class="file-thumb" />
              <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="flex-shrink:0;opacity:.6">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
              </svg>
              <span class="file-name">{{ selectedFile.name }}</span>
              <button @click="selectedFile = null; filePreviewUrl = null" class="file-remove">&times;</button>
            </div>
          </div>

        </template>
      </div>

      </div>
      </main>
    </div>

    <!-- Image lightbox -->
    <div v-if="lightboxUrl" class="lightbox" @click="lightboxUrl = null">
      <img :src="lightboxUrl" class="lightbox-img" />
    </div>

    <!-- ── Interactive Message Builder modal ─────────────────────────── -->
    <div v-if="showInteractiveBuilder" class="modal-overlay" @click.self="showInteractiveBuilder = false">
      <div class="interactive-modal">

        <!-- Header -->
        <div class="im-header">
          <h3 class="im-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align:-3px">
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
            </svg>
            Interactive Message
          </h3>
          <button class="im-close" @click="showInteractiveBuilder = false">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <!-- Type tabs -->
        <div class="im-tabs">
          <button
            v-for="tab in interactiveTabs"
            :key="tab.type"
            class="im-tab"
            :class="{ 'im-tab-active': interactiveType === tab.type }"
            @click="interactiveType = tab.type"
          >
            <span class="im-tab-icon">{{ tab.icon }}</span>
            {{ tab.label }}
          </button>
        </div>

        <!-- Body: builder (left) + preview (right) -->
        <div class="im-body">

          <!-- ════ BUILDER PANEL ════ -->
          <div class="im-builder">

            <!-- ── Inline Keyboard ── -->
            <template v-if="interactiveType === 'inline_keyboard'">
              <label class="im-label">Message text <span class="im-required">*</span></label>
              <textarea v-model="interactiveText" class="im-textarea" placeholder="Type the message to send with the buttons…" rows="3"></textarea>

              <label class="im-label" style="margin-top:14px">Button rows</label>
              <div class="im-rows-list">
                <div v-for="(row, ri) in inlineRows" :key="ri" class="im-row-block">
                  <div class="im-row-label">Row {{ ri + 1 }}</div>
                  <div class="im-btn-list">
                    <div v-for="(btn, bi) in row" :key="bi" class="im-btn-editor">
                      <input v-model="btn.label" class="im-input" placeholder="Button label" />
                      <select v-model="btn.type" class="im-select">
                        <option value="callback">Callback</option>
                        <option value="url">URL link</option>
                      </select>
                      <input
                        v-model="btn.value"
                        class="im-input"
                        :placeholder="btn.type === 'url' ? 'https://…' : 'callback_data'"
                      />
                      <button
                        v-if="row.length > 1"
                        class="im-remove-btn"
                        @click="removeInlineBtn(ri, bi)"
                        title="Remove button"
                      >×</button>
                    </div>
                  </div>
                  <div class="im-row-actions">
                    <button class="im-add-link" @click="addInlineBtn(ri)">+ Button</button>
                    <button v-if="inlineRows.length > 1" class="im-remove-link" @click="removeInlineRow(ri)">× Row</button>
                  </div>
                </div>
              </div>
              <button class="im-add-row-btn" @click="addInlineRow">+ Add Row</button>
            </template>

            <!-- ── Reply Keyboard ── -->
            <template v-else-if="interactiveType === 'reply_keyboard'">
              <label class="im-label">Message text <span class="im-required">*</span></label>
              <textarea v-model="interactiveText" class="im-textarea" placeholder="Message shown above the keyboard…" rows="3"></textarea>

              <label class="im-label" style="margin-top:14px">Keyboard rows</label>
              <div class="im-rows-list">
                <div v-for="(row, ri) in replyRows" :key="ri" class="im-row-block">
                  <div class="im-row-label">Row {{ ri + 1 }}</div>
                  <div class="im-btn-list">
                    <div v-for="(btn, bi) in row" :key="bi" class="im-btn-editor im-reply-btn-editor">
                      <input v-model="btn.label" class="im-input" placeholder="Button label" />
                      <label class="im-checkbox-label" title="Request user's phone number">
                        <input type="checkbox" v-model="btn.requestContact" @change="btn.requestLocation = false" />
                        📱 Contact
                      </label>
                      <label class="im-checkbox-label" title="Request user's location">
                        <input type="checkbox" v-model="btn.requestLocation" @change="btn.requestContact = false" />
                        📍 Location
                      </label>
                      <button v-if="row.length > 1" class="im-remove-btn" @click="removeReplyBtn(ri, bi)" title="Remove">×</button>
                    </div>
                  </div>
                  <div class="im-row-actions">
                    <button class="im-add-link" @click="addReplyBtn(ri)">+ Button</button>
                    <button v-if="replyRows.length > 1" class="im-remove-link" @click="removeReplyRow(ri)">× Row</button>
                  </div>
                </div>
              </div>
              <button class="im-add-row-btn" @click="addReplyRow">+ Add Row</button>
            </template>

            <!-- ── Poll ── -->
            <template v-else-if="interactiveType === 'poll'">
              <label class="im-label">Question <span class="im-required">*</span></label>
              <textarea v-model="pollQuestion" class="im-textarea" placeholder="What do you want to ask?" rows="2" maxlength="300"></textarea>
              <div class="im-char-count">{{ pollQuestion.length }}/300</div>

              <label class="im-label" style="margin-top:12px">Options <span class="im-required">*</span> <small>(2–10)</small></label>
              <div class="im-poll-options">
                <div v-for="(opt, oi) in pollOptions" :key="oi" class="im-poll-option-row">
                  <span class="im-opt-num">{{ oi + 1 }}.</span>
                  <input v-model="pollOptions[oi]" class="im-input" :placeholder="`Option ${oi + 1}`" maxlength="100" />
                  <button v-if="pollOptions.length > 2" class="im-remove-btn" @click="removePollOption(oi)">×</button>
                </div>
              </div>
              <button v-if="pollOptions.length < 10" class="im-add-link" @click="addPollOption" style="margin-top:6px">+ Add option</button>

              <div class="im-poll-settings">
                <label class="im-checkbox-label">
                  <input type="checkbox" v-model="pollAnonymous" />
                  Anonymous voting
                </label>
                <label class="im-checkbox-label">
                  <input type="checkbox" v-model="pollMultipleAnswers" />
                  Allow multiple answers
                </label>
              </div>

              <label class="im-label" style="margin-top:10px">Auto-close after (minutes) <small>optional</small></label>
              <input v-model="pollOpenPeriod" class="im-input" type="number" min="1" max="10080" placeholder="e.g. 60" style="max-width:140px" />
            </template>

            <!-- ── Quiz ── -->
            <template v-else-if="interactiveType === 'quiz'">
              <label class="im-label">Question <span class="im-required">*</span></label>
              <textarea v-model="pollQuestion" class="im-textarea" placeholder="What is the correct answer to…?" rows="2" maxlength="300"></textarea>
              <div class="im-char-count">{{ pollQuestion.length }}/300</div>

              <label class="im-label" style="margin-top:12px">Options <span class="im-required">*</span> <small>(2–10)</small></label>
              <div class="im-poll-options">
                <div v-for="(opt, oi) in pollOptions" :key="oi" class="im-poll-option-row">
                  <input
                    type="radio"
                    :value="oi"
                    v-model="quizCorrectOption"
                    class="im-radio"
                    :title="`Mark option ${oi + 1} as correct`"
                  />
                  <input v-model="pollOptions[oi]" class="im-input" :placeholder="`Option ${oi + 1}`" maxlength="100" />
                  <button v-if="pollOptions.length > 2" class="im-remove-btn" @click="removePollOption(oi)">×</button>
                </div>
              </div>
              <div class="im-quiz-hint">Click the radio button to mark the correct answer</div>
              <button v-if="pollOptions.length < 10" class="im-add-link" @click="addPollOption" style="margin-top:6px">+ Add option</button>

              <label class="im-label" style="margin-top:12px">Explanation <small>shown after wrong answer, optional</small></label>
              <textarea v-model="quizExplanation" class="im-textarea" placeholder="Explain the correct answer…" rows="2" maxlength="200"></textarea>

              <label class="im-checkbox-label" style="margin-top:10px">
                <input type="checkbox" v-model="pollAnonymous" />
                Anonymous
              </label>
            </template>

          </div><!-- end .im-builder -->

          <!-- ════ PREVIEW PANEL ════ -->
          <div class="im-preview">
            <div class="im-preview-label">Live Preview</div>
            <div class="preview-frame">

              <!-- Inline Keyboard preview -->
              <template v-if="interactiveType === 'inline_keyboard'">
                <div class="preview-bubble preview-bubble-bot">
                  {{ interactiveText || 'Your message text…' }}
                </div>
                <div class="preview-inline-grid">
                  <div v-for="(row, ri) in inlineRows" :key="ri" class="preview-inline-row">
                    <div
                      v-for="(btn, bi) in row"
                      :key="bi"
                      class="preview-inline-btn"
                      :class="{ 'preview-btn-url': btn.type === 'url' }"
                    >
                      <svg v-if="btn.type === 'url'" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                      </svg>
                      {{ btn.label || 'Button' }}
                    </div>
                  </div>
                </div>
              </template>

              <!-- Reply Keyboard preview -->
              <template v-else-if="interactiveType === 'reply_keyboard'">
                <div class="preview-bubble preview-bubble-bot">
                  {{ interactiveText || 'Your message text…' }}
                </div>
                <div class="preview-reply-keyboard">
                  <div v-for="(row, ri) in replyRows" :key="ri" class="preview-reply-row">
                    <div
                      v-for="(btn, bi) in row"
                      :key="bi"
                      class="preview-reply-btn"
                    >
                      <span v-if="btn.requestContact">📱 </span>
                      <span v-else-if="btn.requestLocation">📍 </span>
                      {{ btn.label || 'Button' }}
                    </div>
                  </div>
                </div>
              </template>

              <!-- Poll preview -->
              <template v-else-if="interactiveType === 'poll'">
                <div class="preview-poll-card">
                  <div class="preview-poll-header">
                    <span class="preview-poll-icon">📊</span>
                    <span class="preview-poll-type">{{ pollAnonymous ? 'Anonymous Poll' : 'Public Poll' }}</span>
                  </div>
                  <div class="preview-poll-question">{{ pollQuestion || 'Your poll question?' }}</div>
                  <div class="preview-poll-options">
                    <div v-for="(opt, oi) in pollOptions" :key="oi" class="preview-poll-option">
                      <div class="preview-poll-radio"></div>
                      <span>{{ opt || `Option ${oi + 1}` }}</span>
                    </div>
                  </div>
                  <div class="preview-poll-footer">
                    <button class="preview-vote-btn">Vote</button>
                    <span class="preview-vote-count">0 votes</span>
                  </div>
                </div>
              </template>

              <!-- Quiz preview -->
              <template v-else-if="interactiveType === 'quiz'">
                <div class="preview-poll-card preview-quiz-card">
                  <div class="preview-poll-header">
                    <span class="preview-poll-icon">🧠</span>
                    <span class="preview-poll-type">Quiz</span>
                  </div>
                  <div class="preview-poll-question">{{ pollQuestion || 'Your quiz question?' }}</div>
                  <div class="preview-poll-options">
                    <div
                      v-for="(opt, oi) in pollOptions"
                      :key="oi"
                      class="preview-poll-option"
                      :class="{ 'preview-opt-correct': oi === quizCorrectOption }"
                    >
                      <div class="preview-poll-radio" :class="{ 'preview-radio-correct': oi === quizCorrectOption }">
                        <svg v-if="oi === quizCorrectOption" width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      </div>
                      <span>{{ opt || `Option ${oi + 1}` }}</span>
                      <span v-if="oi === quizCorrectOption" class="preview-correct-tag">Correct</span>
                    </div>
                  </div>
                  <div v-if="quizExplanation" class="preview-explanation">
                    💡 {{ quizExplanation }}
                  </div>
                  <div class="preview-poll-footer">
                    <button class="preview-vote-btn">Submit</button>
                  </div>
                </div>
              </template>

            </div><!-- end .preview-frame -->
          </div><!-- end .im-preview -->

        </div><!-- end .im-body -->

        <!-- Footer: send action -->
        <div class="im-footer">
          <button class="im-btn-cancel" @click="showInteractiveBuilder = false">Cancel</button>
          <button
            class="im-btn-send"
            :disabled="sending"
            @click="sendInteractiveMessage"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" style="flex-shrink:0">
              <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
            </svg>
            Send Message
          </button>
        </div>

      </div><!-- end .interactive-modal -->
    </div>

    <!-- ── Forward modal ─────────────────────────────────────────────── -->
    <div v-if="showForwardModal" class="modal-overlay" @click.self="showForwardModal = false">
      <div class="forward-modal">
        <div class="forward-modal-header">
          <h3>Forward to...</h3>
          <button class="fwd-close-btn" @click="showForwardModal = false">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <!-- Preview of message being forwarded -->
        <div v-if="forwardMsg" class="forward-preview">
          <div class="forward-preview-icon">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="15 17 20 12 15 7"/><path d="M4 18v-2a4 4 0 0 1 4-4h12"/>
            </svg>
          </div>
          <span class="forward-preview-text">{{ forwardMsg.message || '[Media]' }}</span>
        </div>

        <!-- Search contacts -->
        <div class="forward-search-box">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            v-model="forwardSearch"
            type="text"
            placeholder="Search contacts..."
            class="forward-search-input"
            autofocus
          />
        </div>

        <!-- Contact list -->
        <div class="forward-contacts-list">
          <div v-if="forwarding" class="fwd-state">
            <div class="spinner spinner-sm"></div>
            <span>Forwarding…</span>
          </div>
          <div
            v-else-if="filteredForwardContacts.length === 0"
            class="fwd-state fwd-empty"
          >
            No other contacts found
          </div>
          <div
            v-else
            v-for="c in filteredForwardContacts"
            :key="`${c.telegram_user_id}_${c.channel_id}`"
            class="forward-contact-item"
            @click="confirmForward(c)"
          >
            <div class="fwd-avatar" :style="{ background: avatarColor(c) }">
              <img v-if="c.profile_pic" :src="c.profile_pic" class="avatar-img" />
              <span v-else class="avatar-initials">{{ initials(c) }}</span>
            </div>
            <div class="fwd-contact-info">
              <span class="fwd-contact-name">{{ contactName(c) }}</span>
              <span v-if="c.username" class="fwd-contact-username">@{{ c.username }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Toast notification ─────────────────────────────────────────── -->
    <transition name="toast-slide">
      <div v-if="showToast" class="tg-toast">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
        {{ toastMsg }}
      </div>
    </transition>

  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import { DashboardService } from '@/services/dashboard'
import { io } from 'socket.io-client'
import telegramLogo from '@/assets/telegram-logo.svg'

const SOCKET_URL = 'https://telegram.quali-d.com'

const AVATAR_COLORS = [
  '#229ED9', '#5B5EA6', '#9B2335', '#EFC050', '#45B8AC',
  '#DD4132', '#009B77', '#B163A3', '#955251', '#F7CAC9'
]

export default {
  name: 'TelegramView',
  components: { Sidebar, TopHeader },

  data () {
    return {
      telegramLogo,

      // Bots
      bots: [],
      selectedBotId: '',

      // Inbox
      contacts: [],
      loadingInbox: true,
      searchQuery: '',
      searchTimeout: null,
      showArchived: false,
      unreadCount: 0,

      // Conversation
      selectedContact: null,
      messages: [],
      loadingMessages: false,
      loadingMoreMessages: false,
      hasMoreMessages: false,

      // Send
      sendText: '',
      sending: false,
      requestingPhone: false,
      selectedFile: null,
      filePreviewUrl: null,
      replyingTo: null,

      // Quick replies
      quickReplies: [],
      loadingQuickReplies: false,
      showQuickRepliesDropdown: false,

      // Notes
      showNotesPanel: false,
      savingNotes: false,

      // Interactive Message Builder
      showInteractiveBuilder: false,
      interactiveType: 'inline_keyboard',
      interactiveText: '',

      // Inline keyboard builder
      inlineRows: [[{ label: '', type: 'callback', value: '' }]],

      // Reply keyboard builder
      replyRows: [[{ label: '', requestContact: false, requestLocation: false }]],

      // Poll / Quiz shared
      pollQuestion: '',
      pollOptions: ['', ''],
      pollAnonymous: true,
      pollMultipleAnswers: false,
      pollOpenPeriod: '',
      quizCorrectOption: 0,
      quizExplanation: '',

      // Lightbox
      lightboxUrl: null,

      // Message hover actions
      hoveredMsgId: null,

      // Forward modal
      showForwardModal: false,
      forwardMsg: null,
      forwardSearch: '',
      forwarding: false,

      // Toast
      showToast: false,
      toastMsg: '',
      toastTimer: null,

      // Socket
      socket: null
    }
  },

  computed: {
    interactiveTabs () {
      return [
        { type: 'inline_keyboard', icon: '⬜', label: 'Inline Buttons' },
        { type: 'reply_keyboard', icon: '⌨️', label: 'Quick Reply' },
        { type: 'poll', icon: '📊', label: 'Poll' },
        { type: 'quiz', icon: '🧠', label: 'Quiz' }
      ]
    },

    filteredForwardContacts () {
      const q = this.forwardSearch.trim().toLowerCase()
      return this.contacts.filter(c => {
        // Exclude the contact currently open
        if (
          this.selectedContact &&
          c.telegram_user_id === this.selectedContact.telegram_user_id &&
          c.channel_id === this.selectedContact.channel_id
        ) return false
        if (!q) return true
        const name = this.contactName(c).toLowerCase()
        const uname = (c.username || '').toLowerCase()
        return name.includes(q) || uname.includes(q)
      })
    }
  },

  async mounted () {
    document.addEventListener('click', this.handleClickOutside)
    await Promise.all([this.loadBots(), this.loadInbox(), this.loadUnreadCount(), this.loadQuickReplies()])
    this.connectSocket()
  },

  beforeUnmount () {
    document.removeEventListener('click', this.handleClickOutside)
    if (this.socket) this.socket.disconnect()
  },

  methods: {
    // ── Bots ──────────────────────────────────────────────────────────
    async loadBots () {
      try {
        const res = await DashboardService.getTelegramBots()
        if (res.success) this.bots = res.bots || []
      } catch (_) {}
    },

    // ── Inbox ──────────────────────────────────────────────────────────
    async loadInbox () {
      this.loadingInbox = true
      try {
        const opts = { archived: this.showArchived ? 1 : 0, limit: 60 }
        if (this.selectedBotId) opts.channelId = this.selectedBotId
        if (this.searchQuery) opts.search = this.searchQuery
        const res = await DashboardService.getTelegramInbox(opts)
        if (res.success) this.contacts = res.contacts || []
      } catch (err) {
        console.error('Failed to load inbox:', err)
      } finally {
        this.loadingInbox = false
      }
    },

    async loadUnreadCount () {
      try {
        const res = await DashboardService.getTelegramUnreadCount()
        if (res.success) this.unreadCount = res.unread_count || 0
      } catch (_) {}
    },

    onSearch () {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => this.loadInbox(), 400)
    },

    toggleArchived () {
      this.showArchived = !this.showArchived
      this.loadInbox()
    },

    // ── Contact selection ──────────────────────────────────────────────
    isSelected (contact) {
      return this.selectedContact &&
        this.selectedContact.telegram_user_id === contact.telegram_user_id &&
        this.selectedContact.channel_id === contact.channel_id
    },

    async selectContact (contact) {
      this.selectedContact = contact
      this.messages = []
      this.loadingMessages = true
      this.loadingMoreMessages = false
      this.hasMoreMessages = false
      this.replyingTo = null
      this.sendText = ''
      this.selectedFile = null
      this.filePreviewUrl = null
      this.showInteractiveBuilder = false

      try {
        const res = await DashboardService.getTelegramConversation(
          contact.telegram_user_id,
          contact.channel_id,
          { limit: 50 }
        )
        if (res.success) {
          this.messages = res.messages || []
          this.hasMoreMessages = !!res.has_more
        }
      } catch (err) {
        console.error('Failed to load conversation:', err)
      } finally {
        this.loadingMessages = false
        // nextTick ensures DOM (messages list) is rendered before we scroll
        this.$nextTick(() => this.scrollToBottom())
      }

      // Mark resolved (update UI)
      if (contact.case_resolved === 0) {
        const idx = this.contacts.findIndex(c =>
          c.telegram_user_id === contact.telegram_user_id && c.channel_id === contact.channel_id
        )
        if (idx !== -1) this.contacts[idx].case_resolved = 1
      }
    },

    // ── Resolve / Archive ──────────────────────────────────────────────
    async toggleResolved () {
      if (!this.selectedContact) return
      const wasResolved = this.selectedContact.case_resolved
      try {
        if (wasResolved) {
          await DashboardService.unresolveTelegramContact(
            this.selectedContact.telegram_user_id,
            this.selectedContact.channel_id
          )
        } else {
          await DashboardService.resolveTelegramContact(
            this.selectedContact.telegram_user_id,
            this.selectedContact.channel_id
          )
        }
        this.selectedContact.case_resolved = wasResolved ? 0 : 1
        await this.loadUnreadCount()
      } catch (err) {
        console.error('Toggle resolved failed:', err)
      }
    },

    async toggleArchive () {
      if (!this.selectedContact) return
      const wasArchived = this.selectedContact.is_archived
      try {
        if (wasArchived) {
          await DashboardService.unarchiveTelegramContact(
            this.selectedContact.telegram_user_id,
            this.selectedContact.channel_id
          )
        } else {
          await DashboardService.archiveTelegramContact(
            this.selectedContact.telegram_user_id,
            this.selectedContact.channel_id
          )
        }
        // Remove from current view if doesn't belong
        this.contacts = this.contacts.filter(c =>
          !(c.telegram_user_id === this.selectedContact.telegram_user_id &&
            c.channel_id === this.selectedContact.channel_id)
        )
        this.selectedContact = null
      } catch (err) {
        console.error('Toggle archive failed:', err)
      }
    },

    // ── Quick replies ──────────────────────────────────────────────────
    async loadQuickReplies () {
      this.loadingQuickReplies = true
      try {
        const res = await DashboardService.getQuickReplies()
        if (res.success) this.quickReplies = res.quick_replies || []
      } catch (_) {
      } finally {
        this.loadingQuickReplies = false
      }
    },

    async toggleQuickReplies () {
      this.showQuickRepliesDropdown = !this.showQuickRepliesDropdown
      if (this.showQuickRepliesDropdown && !this.quickReplies.length && !this.loadingQuickReplies) {
        await this.loadQuickReplies()
      }
    },

    useQuickReply (reply) {
      this.sendText = reply.message || ''
      this.showQuickRepliesDropdown = false
      this.$nextTick(() => {
        if (this.$refs.sendInputRef) this.$refs.sendInputRef.focus()
      })
    },

    handleClickOutside (e) {
      const el = this.$el?.querySelector('.quick-replies-wrapper')
      if (el && !el.contains(e.target)) this.showQuickRepliesDropdown = false
    },

    // ── Customer notes ─────────────────────────────────────────────────
    async saveNotes () {
      if (!this.selectedContact) return
      this.savingNotes = true
      try {
        await DashboardService.saveTelegramContactNotes(
          this.selectedContact.telegram_user_id,
          this.selectedContact.channel_id,
          this.selectedContact.notes || ''
        )
      } catch (err) {
        console.error('Failed to save notes:', err)
      } finally {
        this.savingNotes = false
      }
    },

    // ── Block / Unblock ────────────────────────────────────────────────
    async toggleBlock () {
      if (!this.selectedContact) return
      const wasBlocked = this.selectedContact.is_blocked
      try {
        if (wasBlocked) {
          await DashboardService.unblockTelegramContact(
            this.selectedContact.telegram_user_id,
            this.selectedContact.channel_id
          )
        } else {
          await DashboardService.blockTelegramContact(
            this.selectedContact.telegram_user_id,
            this.selectedContact.channel_id
          )
        }
        this.selectedContact.is_blocked = wasBlocked ? 0 : 1
        const idx = this.contacts.findIndex(c =>
          c.telegram_user_id === this.selectedContact.telegram_user_id &&
          c.channel_id === this.selectedContact.channel_id
        )
        if (idx !== -1) this.contacts[idx].is_blocked = this.selectedContact.is_blocked
      } catch (err) {
        console.error('Toggle block failed:', err)
      }
    },

    // ── Request phone number ───────────────────────────────────────────
    async requestPhoneNumber () {
      if (!this.selectedContact || this.requestingPhone) return
      this.requestingPhone = true
      try {
        await DashboardService.sendTelegramMessage({
          channelId: this.selectedContact.channel_id,
          telegramUserId: this.selectedContact.telegram_user_id,
          messageObject: {
            type: 'reply_keyboard',
            text: 'Please share your phone number so we can better assist you.',
            keyboard: [[{ text: '📱 Share Phone Number', request_contact: true }]],
            one_time: true
          }
        })
      } catch (err) {
        console.error('Request phone failed:', err)
      } finally {
        this.requestingPhone = false
      }
    },

    // ── Message hover actions ──────────────────────────────────────────
    startReply (msg) {
      this.replyingTo = msg
      this.$nextTick(() => {
        if (this.$refs.sendInputRef) this.$refs.sendInputRef.focus()
      })
    },

    startForward (msg) {
      this.forwardMsg = msg
      this.forwardSearch = ''
      this.forwarding = false
      this.showForwardModal = true
    },

    async confirmForward (targetContact) {
      if (!this.forwardMsg || !this.selectedContact || this.forwarding) return
      this.forwarding = true
      try {
        const res = await DashboardService.forwardTelegramMessages({
          channelId: this.selectedContact.channel_id,
          fromUserId: this.selectedContact.telegram_user_id,
          toUserId: targetContact.telegram_user_id,
          messageIds: [this.forwardMsg.telegram_message_id]
        })
        if (res.success) {
          this.showForwardModal = false
          this.showToastMsg(`Forwarded to ${this.contactName(targetContact)}`)
        } else {
          this.showToastMsg('Forward failed: ' + (res.message || 'Unknown error'))
        }
      } catch (err) {
        this.showToastMsg('Forward failed')
      } finally {
        this.forwarding = false
      }
    },

    async quickAddToNotes (msg) {
      if (!this.selectedContact || !msg.message) return
      const timestamp = new Date().toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })
      const entry = `[${timestamp}] ${msg.message}`
      const existing = (this.selectedContact.notes || '').trim()
      this.selectedContact.notes = existing ? `${existing}\n${entry}` : entry
      try {
        await DashboardService.saveTelegramContactNotes(
          this.selectedContact.telegram_user_id,
          this.selectedContact.channel_id,
          this.selectedContact.notes
        )
        this.showToastMsg('Added to notes')
      } catch (_) {
        this.showToastMsg('Failed to save note')
      }
    },

    showToastMsg (text) {
      this.toastMsg = text
      this.showToast = true
      clearTimeout(this.toastTimer)
      this.toastTimer = setTimeout(() => { this.showToast = false }, 3000)
    },

    // ── Send message ───────────────────────────────────────────────────
    async sendMessage () {
      if (!this.selectedContact) return
      if (this.sending) return
      if (!this.sendText.trim() && !this.selectedFile) return

      this.sending = true
      const tempId = `tmp_${Date.now()}`

      try {
        let messageObject
        let optimisticMsg

        if (this.selectedFile) {
          const file = this.selectedFile
          const caption = this.sendText.trim() || undefined
          const attType = file.type.startsWith('image/') ? 'photo' : 'document'
          // Use blob URL for instant preview — swapped out for real URL after upload
          const localUrl = this.filePreviewUrl || URL.createObjectURL(file)

          optimisticMsg = {
            id: tempId,
            conversation_role: 'Agent',
            attachment_type: attType,
            image_url: attType === 'photo' ? localUrl : null,
            document_url: attType === 'document' ? localUrl : null,
            message: caption || null,
            created_at: new Date().toISOString(),
            sending: true
          }

          // Show preview in bubble immediately before upload starts
          this.messages.push(optimisticMsg)
          this.$nextTick(() => this.scrollToBottom())
          this.sendText = ''
          this.selectedFile = null
          this.filePreviewUrl = null
          this.replyingTo = null

          // Upload to server
          const formData = new FormData()
          formData.append('file', file)
          const uploadToken = localStorage.getItem('token')
          const uploadRes = await fetch('https://api.quali-d.com/telegram/upload', {
            method: 'POST',
            headers: { Authorization: `Bearer ${uploadToken}` },
            body: formData
          })
          const uploadData = await uploadRes.json()
          const fileUrl = uploadData?.url
          if (!fileUrl) throw new Error(uploadData?.message || 'Upload failed')

          // Swap blob URL → permanent B2 URL
          const previewIdx = this.messages.findIndex(m => m.id === tempId)
          if (previewIdx !== -1) {
            if (attType === 'photo') this.messages[previewIdx].image_url = fileUrl
            else this.messages[previewIdx].document_url = fileUrl
          }
          URL.revokeObjectURL(localUrl)
          messageObject = { type: attType, url: fileUrl, caption }
        } else {
          const replyToId = this.replyingTo?.telegram_message_id || null
          messageObject = {
            type: 'text',
            text: this.sendText.trim(),
            ...(replyToId ? { reply_to_message_id: replyToId } : {})
          }
          optimisticMsg = {
            id: tempId,
            conversation_role: 'Agent',
            message: this.sendText.trim(),
            reply_to_snapshot: this.replyingTo
              ? { sender: 'You', text: this.replyingTo.message || null }
              : null,
            created_at: new Date().toISOString(),
            sending: true
          }

          // Optimistic update for text
          this.messages.push(optimisticMsg)
          this.$nextTick(() => this.scrollToBottom())
          this.sendText = ''
          this.selectedFile = null
          this.filePreviewUrl = null
          this.replyingTo = null
        }

        const res = await DashboardService.sendTelegramMessage({
          channelId: this.selectedContact.channel_id,
          telegramUserId: this.selectedContact.telegram_user_id,
          messageObject
        })

        // Replace optimistic with real
        const idx = this.messages.findIndex(m => m.id === tempId)
        if (idx !== -1) {
          this.messages[idx].sending = false
          this.messages[idx].telegram_message_id = res?.telegram_message_id || null
        }

        this.updateContactLastMessage(this.selectedContact, optimisticMsg)
      } catch (err) {
        console.error('Send failed:', err)
        const failIdx = this.messages.findIndex(m => m.id === tempId)
        if (failIdx !== -1) {
          this.messages[failIdx].sending = false
          this.messages[failIdx].failed = true
        }
      } finally {
        this.sending = false
      }
    },

    // ── Interactive Message Builder ────────────────────────────────────
    openInteractiveBuilder () {
      this.interactiveText = this.sendText.trim()
      this.interactiveType = 'inline_keyboard'
      this.inlineRows = [[{ label: '', type: 'callback', value: '' }]]
      this.replyRows = [[{ label: '', requestContact: false, requestLocation: false }]]
      this.pollQuestion = ''
      this.pollOptions = ['', '']
      this.pollAnonymous = true
      this.pollMultipleAnswers = false
      this.pollOpenPeriod = ''
      this.quizCorrectOption = 0
      this.quizExplanation = ''
      this.showInteractiveBuilder = true
    },

    // Inline keyboard helpers
    addInlineRow () {
      this.inlineRows.push([{ label: '', type: 'callback', value: '' }])
    },
    addInlineBtn (ri) {
      this.inlineRows[ri].push({ label: '', type: 'callback', value: '' })
    },
    removeInlineRow (ri) {
      if (this.inlineRows.length > 1) this.inlineRows.splice(ri, 1)
    },
    removeInlineBtn (ri, bi) {
      if (this.inlineRows[ri].length > 1) this.inlineRows[ri].splice(bi, 1)
    },

    // Reply keyboard helpers
    addReplyRow () {
      this.replyRows.push([{ label: '', requestContact: false, requestLocation: false }])
    },
    addReplyBtn (ri) {
      this.replyRows[ri].push({ label: '', requestContact: false, requestLocation: false })
    },
    removeReplyRow (ri) {
      if (this.replyRows.length > 1) this.replyRows.splice(ri, 1)
    },
    removeReplyBtn (ri, bi) {
      if (this.replyRows[ri].length > 1) this.replyRows[ri].splice(bi, 1)
    },

    // Poll / quiz helpers
    addPollOption () {
      if (this.pollOptions.length < 10) this.pollOptions.push('')
    },
    removePollOption (i) {
      if (this.pollOptions.length > 2) this.pollOptions.splice(i, 1)
      if (this.quizCorrectOption >= this.pollOptions.length) {
        this.quizCorrectOption = this.pollOptions.length - 1
      }
    },

    async sendInteractiveMessage () {
      if (!this.selectedContact || this.sending) return

      let messageObject
      const type = this.interactiveType

      if (type === 'inline_keyboard') {
        if (!this.interactiveText.trim()) return
        const inlineKeyboard = this.inlineRows.map(row =>
          row.filter(b => b.label.trim()).map(b =>
            b.type === 'url'
              ? { text: b.label, url: b.value }
              : { text: b.label, callback_data: b.value || b.label }
          )
        ).filter(r => r.length > 0)
        messageObject = {
          type: 'inline_keyboard',
          text: this.interactiveText.trim(),
          inline_keyboard: inlineKeyboard
        }
      } else if (type === 'reply_keyboard') {
        if (!this.interactiveText.trim()) return
        const keyboard = this.replyRows.map(row =>
          row.filter(b => b.label.trim()).map(b => ({
            text: b.label,
            ...(b.requestContact ? { request_contact: true } : {}),
            ...(b.requestLocation ? { request_location: true } : {})
          }))
        ).filter(r => r.length > 0)
        messageObject = {
          type: 'reply_keyboard',
          text: this.interactiveText.trim(),
          keyboard,
          one_time: false
        }
      } else if (type === 'poll') {
        if (!this.pollQuestion.trim()) return
        const validOptions = this.pollOptions.filter(o => o.trim())
        if (validOptions.length < 2) return
        messageObject = {
          type: 'poll',
          question: this.pollQuestion.trim(),
          options: validOptions,
          is_anonymous: this.pollAnonymous,
          allows_multiple_answers: this.pollMultipleAnswers,
          ...(this.pollOpenPeriod ? { open_period: parseInt(this.pollOpenPeriod) * 60 } : {})
        }
      } else if (type === 'quiz') {
        if (!this.pollQuestion.trim()) return
        const validOptions = this.pollOptions.filter(o => o.trim())
        if (validOptions.length < 2) return
        messageObject = {
          type: 'quiz',
          question: this.pollQuestion.trim(),
          options: validOptions,
          correct_option_id: this.quizCorrectOption,
          is_anonymous: this.pollAnonymous,
          ...(this.quizExplanation.trim() ? { explanation: this.quizExplanation.trim() } : {})
        }
      } else {
        return
      }

      this.sending = true
      const tempId = `tmp_${Date.now()}`
      const isPollOrQuiz = type === 'poll' || type === 'quiz'

      const optimisticMsg = {
        id: tempId,
        conversation_role: 'Agent',
        attachment_type: type,
        message: isPollOrQuiz
          ? messageObject.question
          : messageObject.text,
        poll_preview: isPollOrQuiz
          ? {
              options: messageObject.options,
              correctOption: type === 'quiz' ? messageObject.correct_option_id : null
            }
          : null,
        created_at: new Date().toISOString(),
        sending: true
      }

      this.messages.push(optimisticMsg)
      this.$nextTick(() => this.scrollToBottom())
      this.showInteractiveBuilder = false

      try {
        const res = await DashboardService.sendTelegramMessage({
          channelId: this.selectedContact.channel_id,
          telegramUserId: this.selectedContact.telegram_user_id,
          messageObject
        })
        const idx = this.messages.findIndex(m => m.id === tempId)
        if (idx !== -1) {
          this.messages[idx].sending = false
          this.messages[idx].telegram_message_id = res?.telegram_message_id || null
        }
        this.updateContactLastMessage(this.selectedContact, optimisticMsg)
      } catch (err) {
        console.error('Interactive send failed:', err)
        const failIdx = this.messages.findIndex(m => m.id === tempId)
        if (failIdx !== -1) {
          this.messages[failIdx].sending = false
          this.messages[failIdx].failed = true
        }
      } finally {
        this.sending = false
      }
    },

    // ── File handling ──────────────────────────────────────────────────
    onFileSelected (e) {
      const file = e.target.files[0]
      e.target.value = ''
      if (!file) return

      if (file.type.startsWith('video/') || file.type.startsWith('audio/')) {
        alert('Only images and documents are supported.')
        return
      }
      const maxMB = file.type.startsWith('image/') ? 5 : 10
      if (file.size > maxMB * 1024 * 1024) {
        alert(`File too large. Maximum size is ${maxMB} MB.`)
        return
      }

      this.selectedFile = file
      this.filePreviewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    },

    // ── Socket.IO ──────────────────────────────────────────────────────
    connectSocket () {
      const token = localStorage.getItem('token')
      this.socket = io(SOCKET_URL, {
        auth: { token },
        transports: ['websocket']
      })

      this.socket.on('connect', () => {
        console.log('[TG Socket] Connected')
      })

      this.socket.on('connect_error', (err) => {
        console.error('[TG Socket] Connection error:', err.message)
      })

      this.socket.on('new_message', (msg) => {
        // Update inbox contact list
        const idx = this.contacts.findIndex(c =>
          c.telegram_user_id === msg.telegram_user_id && c.channel_id === msg.channel_id
        )
        // Play sound for incoming customer messages
        if (msg.conversation_role === 'Customer') {
          try {
            const audio = new Audio('/telegram_notification.mp3')
            audio.volume = 0.6
            audio.play().catch(() => {})
          } catch (_) {}
        }

        if (idx !== -1) {
          const contact = { ...this.contacts[idx] }
          contact.last_message = msg.message
          contact.last_message_time = msg.created_at
          contact.last_attachment_type = msg.attachment_type
          contact.last_role = msg.conversation_role
          if (msg.conversation_role === 'Customer') contact.case_resolved = 0
          // Update profile pic and phone if the server sent fresh data
          if (msg.profile_pic) contact.profile_pic = msg.profile_pic
          if (msg.phone_number) contact.phone_number = msg.phone_number
          this.contacts.splice(idx, 1)
          this.contacts.unshift(contact)
          if (this.isSelected(contact)) {
            this.selectedContact = contact
          }
        } else {
          // New contact — reload inbox
          this.loadInbox()
        }

        // Append to open conversation
        if (
          this.selectedContact &&
          this.selectedContact.telegram_user_id === msg.telegram_user_id &&
          this.selectedContact.channel_id === msg.channel_id &&
          msg.conversation_role === 'Customer'
        ) {
          this.messages.push({
            id: msg.id,
            conversation_role: msg.conversation_role,
            message: msg.message,
            image_url: msg.image_url,
            video_url: msg.video_url,
            document_url: msg.document_url,
            audio_url: msg.audio_url,
            voice_url: msg.voice_url,
            attachment_type: msg.attachment_type,
            telegram_message_id: msg.telegram_message_id,
            reply_to_message_id: msg.reply_to_message_id,
            reply_to_snapshot: msg.reply_to_snapshot,
            created_at: msg.created_at,
            date_time: msg.date_time
          })
          this.$nextTick(() => this.scrollToBottom())
        }

        this.loadUnreadCount()
      })

      // Customer edited their message — update in-place
      this.socket.on('edited_message', (data) => {
        if (
          this.selectedContact &&
          this.selectedContact.telegram_user_id === data.telegram_user_id &&
          this.selectedContact.channel_id === data.channel_id
        ) {
          const msg = this.messages.find(m => m.telegram_message_id === data.telegram_message_id)
          if (msg) {
            msg.message = data.new_text
            msg.is_edited = true
          }
        }
      })

      this.socket.on('disconnect', () => {
        console.log('[TG Socket] Disconnected')
      })
    },

    // ── UI helpers ─────────────────────────────────────────────────────
    contactName (contact) {
      if (!contact) return ''
      const full = [contact.first_name, contact.last_name].filter(Boolean).join(' ')
      return full || contact.username || String(contact.telegram_user_id)
    },

    initials (contact) {
      const name = this.contactName(contact)
      return name.slice(0, 2).toUpperCase()
    },

    avatarColor (contact) {
      const id = contact?.telegram_user_id || 0
      return AVATAR_COLORS[id % AVATAR_COLORS.length]
    },

    attachmentLabel (type) {
      const map = {
        photo: '📷 Photo',
        video: '🎥 Video',
        audio: '🎵 Audio',
        voice: '🎤 Voice',
        document: '📄 Document',
        sticker: '🎭 Sticker',
        location: '📍 Location',
        contact: '👤 Contact',
        callback_query: '🔘 Button tap',
        poll_answer: '📊 Poll answer',
        reply_keyboard: '⌨️ Keyboard',
        inline_keyboard: '⌨️ Inline keyboard'
      }
      return map[type] || `[${type}]`
    },

    formatTime (dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      const now = new Date()
      const diff = now - d
      if (diff < 86400000) {
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }
      return d.toLocaleDateString([], { month: 'short', day: 'numeric' })
    },

    formatMsgTime (dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },

    formatMessageText (text) {
      if (!text) return ''
      // Basic link detection
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank" class="msg-link">$1</a>')
        .replace(/\n/g, '<br>')
    },

    scrollToBottom () {
      const el = this.$refs.messagesAreaRef
      if (el) el.scrollTop = el.scrollHeight
    },

    onMessagesScroll () {
      const el = this.$refs.messagesAreaRef
      if (!el) return
      if (el.scrollTop < 80 && this.hasMoreMessages && !this.loadingMoreMessages) {
        this.loadMoreMessages()
      }
    },

    async loadMoreMessages () {
      if (!this.hasMoreMessages || this.loadingMoreMessages || !this.selectedContact) return
      const firstMsg = this.messages[0]
      if (!firstMsg) return

      this.loadingMoreMessages = true
      const el = this.$refs.messagesAreaRef
      const prevScrollHeight = el ? el.scrollHeight : 0

      try {
        const res = await DashboardService.getTelegramConversation(
          this.selectedContact.telegram_user_id,
          this.selectedContact.channel_id,
          { limit: 50, beforeId: firstMsg.id }
        )
        if (res.success) {
          const older = res.messages || []
          this.hasMoreMessages = !!res.has_more
          if (older.length > 0) {
            this.messages = [...older, ...this.messages]
            await this.$nextTick()
            // Anchor scroll so the previously-visible messages stay in view
            if (el) el.scrollTop = el.scrollHeight - prevScrollHeight
          }
        }
      } catch (err) {
        console.error('Failed to load more messages:', err)
      } finally {
        this.loadingMoreMessages = false
      }
    },

    openImage (url) {
      this.lightboxUrl = url
    },

    updateContactLastMessage (contact, msg) {
      const idx = this.contacts.findIndex(c =>
        c.telegram_user_id === contact.telegram_user_id && c.channel_id === contact.channel_id
      )
      if (idx !== -1) {
        this.contacts[idx].last_message = msg.message
        this.contacts[idx].last_message_time = msg.created_at
        this.contacts[idx].last_attachment_type = msg.attachment_type
        this.contacts[idx].last_role = 'Agent'
      }
    }
  }
}
</script>

<style scoped>
/* ── Layout (mirrors WhatsApp structure exactly) ─────────────────────── */
.telegram-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
  width: 100%;
  max-width: 100vw;
  overflow-x: hidden;
}
.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
}
.page-content {
  flex: 1;
  padding: 24px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.telegram-container {
  display: flex;
  flex-direction: row;
  flex: 1;
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  overflow: hidden;
  max-height: calc(100vh - 120px);
  min-height: 0;
}

/* ── Inbox panel ─────────────────────────────────────────────────────── */
.inbox-panel {
  width: clamp(260px, 30%, 360px);
  flex-shrink: 0;
  border-right: 1px solid var(--border-primary, #e8e8e8);
  display: flex;
  flex-direction: column;
  background: var(--bg-primary, white);
  overflow: hidden;
}
.inbox-header {
  padding: 14px 14px 10px;
  border-bottom: 1px solid var(--border-primary, #f0f0f0);
  background: var(--bg-primary, white);
}
.inbox-header-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.inbox-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.channel-icon {
  width: 28px; height: 28px;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.inbox-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary, #1a1a2e);
  margin: 0;
}
.unread-badge {
  background: #229ED9;
  color: white;
  font-size: 11px;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 99px;
  min-width: 18px;
  text-align: center;
}
.inbox-controls { display: flex; gap: 4px; }
.control-btn {
  width: 30px; height: 30px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  color: var(--text-secondary, #888);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background .15s, color .15s;
}
.control-btn:hover, .control-btn.active {
  background: rgba(34,158,217,.12);
  color: #229ED9;
}
.bot-selector { margin-bottom: 8px; }
.bot-select {
  width: 100%;
  padding: 7px 10px;
  border: 1px solid var(--border-primary, #e0e0e0);
  border-radius: 8px;
  font-size: 13px;
  color: var(--text-primary, #333);
  background: var(--bg-secondary, #fafafa);
  outline: none;
}
.search-box { position: relative; }
.search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary, #aaa);
}
.search-input {
  width: 100%;
  padding: 8px 10px 8px 32px;
  border: 1px solid var(--border-primary, #e8e8e8);
  border-radius: 8px;
  font-size: 13px;
  background: var(--bg-secondary, #f8f8f8);
  color: var(--text-primary, #1a1a2e);
  outline: none;
  transition: border-color .2s;
  box-sizing: border-box;
}
.search-input:focus { border-color: #229ED9; background: var(--bg-primary, white); }

.contacts-list { flex: 1; overflow-y: auto; }
.list-loading, .list-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 12px;
  color: var(--text-secondary, #bbb);
  font-size: 13px;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  cursor: pointer;
  border-bottom: 1px solid var(--border-primary, #f5f5f5);
  transition: background .15s;
  position: relative;
}
.contact-item:hover { background: var(--bg-secondary, #f8f9fb); }
.contact-item.active { background: rgba(34,158,217,.12); }
.contact-item.unread .contact-name { font-weight: 700; }

.contact-avatar {
  width: 40px; height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}
.avatar-img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; }
.avatar-initials { font-size: 14px; font-weight: 700; color: white; }

.contact-info { flex: 1; min-width: 0; }
.contact-top {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 2px;
}
.contact-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary, #1a1a2e);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.contact-time { font-size: 11px; color: var(--text-secondary, #aaa); flex-shrink: 0; }
.contact-preview {
  font-size: 12px;
  color: var(--text-secondary, #888);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.you-prefix { color: var(--text-secondary, #666); }
.contact-bot { font-size: 11px; color: #229ED9; margin-top: 2px; }
.unread-dot {
  width: 8px; height: 8px;
  background: #229ED9;
  border-radius: 50%;
  flex-shrink: 0;
}

/* ── Chat panel ──────────────────────────────────────────────────────── */
.chat-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--bg-secondary, #f0f2f5);
  min-width: 0;
}
.chat-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: var(--text-secondary, #aaa);
  font-size: 14px;
  background: var(--bg-primary, white);
}

.chat-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: var(--bg-primary, white);
  border-bottom: 1px solid var(--border-primary, #e8e8e8);
  box-shadow: 0 1px 3px rgba(0,0,0,.05);
  flex-shrink: 0;
}
.back-btn {
  display: none;
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--text-secondary, #555);
  padding: 4px;
  border-radius: 6px;
}
.chat-header-avatar {
  width: 38px; height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}
.chat-header-info { flex: 1; min-width: 0; }
.chat-header-name {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary, #1a1a2e);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.chat-header-sub { font-size: 12px; color: var(--text-secondary, #888); }
.chat-header-actions { display: flex; gap: 8px; }
.action-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  border: 1px solid var(--border-primary, #e0e0e0);
  background: var(--bg-primary, white);
  color: var(--text-secondary, #555);
  border-radius: 6px;
  padding: 5px 10px;
  font-size: 12px;
  cursor: pointer;
  transition: all .15s;
}
.action-btn:hover { background: var(--bg-secondary, #f5f5f5); }
.action-btn.action-resolved { color: #065f46; background: #d1fae5; border-color: #a7f3d0; }
.action-btn.action-btn-phone { color: #1d4ed8; border-color: #bfdbfe; background: #eff6ff; }
.action-btn.action-btn-phone:hover { background: #dbeafe; }
.action-btn.action-btn-phone:disabled { opacity: .6; cursor: default; }

/* Messages */
.messages-area {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.messages-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.message-row {
  display: flex;
  flex-direction: column;
  max-width: 70%;
}
.row-agent { align-self: flex-end; align-items: flex-end; }
.row-customer { align-self: flex-start; align-items: flex-start; }

.reply-context {
  display: flex;
  gap: 6px;
  margin-bottom: 4px;
  padding: 4px 8px;
  background: rgba(0,0,0,.06);
  border-radius: 6px;
  max-width: 100%;
}
.reply-bar { width: 3px; background: #229ED9; border-radius: 2px; flex-shrink: 0; }
.reply-content { font-size: 12px; color: var(--text-secondary, #888); overflow: hidden; }
.reply-sender { font-weight: 600; color: #229ED9; display: block; }
.reply-text { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: block; }

.message-bubble {
  padding: 8px 12px;
  border-radius: 12px;
  max-width: 100%;
  position: relative;
  word-break: break-word;
}
.bubble-customer {
  background: var(--bg-primary, white);
  color: var(--text-primary, #1a1a2e);
  border-bottom-left-radius: 3px;
  box-shadow: 0 1px 2px rgba(0,0,0,.08);
}
.bubble-agent {
  background: #229ED9;
  color: white;
  border-bottom-right-radius: 3px;
  box-shadow: 0 1px 2px rgba(34,158,217,.3);
}

.msg-text { margin: 0; font-size: 14px; line-height: 1.4; }
.msg-caption { margin: 6px 0 0; font-size: 13px; }
.msg-time { display: block; font-size: 10px; margin-top: 4px; opacity: .6; text-align: right; }
.msg-link { color: inherit; text-decoration: underline; }
.msg-interactive { font-size: 13px; font-style: italic; opacity: .8; }

.msg-media-img {
  max-width: 260px; max-height: 200px;
  border-radius: 8px; cursor: pointer; display: block; object-fit: cover;
}
.msg-media-video { max-width: 280px; border-radius: 8px; display: block; }
.msg-sticker { width: 120px; height: 120px; object-fit: contain; }
.msg-audio { display: flex; align-items: center; gap: 8px; }
.audio-player { max-width: 200px; }
.msg-document {
  display: flex; align-items: center; gap: 8px; font-size: 13px;
  color: inherit; text-decoration: none; padding: 4px;
  border-radius: 6px; background: rgba(0,0,0,.06);
}

.msg-footer {
  display: flex;
  align-items: center;
  gap: 4px;
  float: right;
  margin-left: 8px;
  margin-top: 2px;
  clear: both;
}
.msg-edited { font-size: 10px; opacity: .6; font-style: italic; }
.msg-status { display: flex; align-items: center; opacity: .75; line-height: 1; }
.msg-status svg { display: block; }

/* ── Interactive Message Builder modal ───────────────────────────────── */
.interactive-modal {
  background: var(--bg-primary, #fff);
  border-radius: 18px;
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 72px rgba(0,0,0,.28);
  overflow: hidden;
  animation: modal-pop .18s ease;
}
.im-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px 14px;
  border-bottom: 1px solid var(--border-primary, #f0f0f0);
  flex-shrink: 0;
}
.im-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary, #1a1a2e);
  display: flex;
  align-items: center;
  gap: 8px;
}
.im-close {
  display: flex; align-items: center; justify-content: center;
  width: 30px; height: 30px;
  border: none; background: transparent;
  color: var(--text-secondary, #888);
  cursor: pointer; border-radius: 50%; padding: 0;
  transition: background .15s;
}
.im-close:hover { background: var(--bg-secondary, #f5f5f5); color: var(--text-primary, #333); }

.im-tabs {
  display: flex;
  gap: 6px;
  padding: 12px 22px;
  border-bottom: 1px solid var(--border-primary, #f0f0f0);
  flex-shrink: 0;
  overflow-x: auto;
}
.im-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  border: 1px solid var(--border-primary, #e0e0e0);
  border-radius: 20px;
  background: var(--bg-secondary, #f8f8f8);
  color: var(--text-secondary, #666);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all .15s;
}
.im-tab:hover { border-color: #229ED9; color: #229ED9; }
.im-tab-active {
  background: #229ED9;
  border-color: #229ED9;
  color: white;
}
.im-tab-icon { font-size: 15px; line-height: 1; }

.im-body {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

/* Left column — builder */
.im-builder {
  width: 55%;
  flex-shrink: 0;
  border-right: 1px solid var(--border-primary, #f0f0f0);
  padding: 18px 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}
.im-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary, #666);
  text-transform: uppercase;
  letter-spacing: .5px;
  margin-bottom: 6px;
}
.im-required { color: #e53e3e; }
.im-textarea {
  width: 100%;
  resize: vertical;
  border: 1px solid var(--border-primary, #ddd);
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  font-family: inherit;
  background: var(--bg-secondary, #f9f9f9);
  color: var(--text-primary, #1a1a2e);
  outline: none;
  box-sizing: border-box;
  transition: border-color .2s;
  line-height: 1.5;
}
.im-textarea:focus { border-color: #229ED9; background: var(--bg-primary, #fff); }
.im-char-count { font-size: 11px; color: var(--text-secondary, #aaa); text-align: right; margin-top: 2px; }
.im-input {
  flex: 1;
  border: 1px solid var(--border-primary, #ddd);
  border-radius: 7px;
  padding: 7px 10px;
  font-size: 13px;
  background: var(--bg-secondary, #f9f9f9);
  color: var(--text-primary, #1a1a2e);
  outline: none;
  transition: border-color .2s;
  min-width: 0;
}
.im-input:focus { border-color: #229ED9; background: var(--bg-primary, #fff); }
.im-select {
  border: 1px solid var(--border-primary, #ddd);
  border-radius: 7px;
  padding: 7px 8px;
  font-size: 12px;
  background: var(--bg-secondary, #f9f9f9);
  color: var(--text-primary, #1a1a2e);
  outline: none;
  cursor: pointer;
  flex-shrink: 0;
}
.im-remove-btn {
  display: flex; align-items: center; justify-content: center;
  width: 24px; height: 24px; flex-shrink: 0;
  border: none; border-radius: 50%;
  background: rgba(220,38,38,.12); color: #dc2626;
  cursor: pointer; font-size: 16px; line-height: 1;
  transition: background .15s;
}
.im-remove-btn:hover { background: rgba(220,38,38,.25); }

.im-rows-list { display: flex; flex-direction: column; gap: 10px; margin-bottom: 8px; }
.im-row-block {
  background: var(--bg-secondary, #f5f7fa);
  border: 1px solid var(--border-primary, #eaeaea);
  border-radius: 10px;
  padding: 10px 12px;
}
.im-row-label { font-size: 11px; font-weight: 600; color: var(--text-secondary, #aaa); margin-bottom: 6px; }
.im-btn-list { display: flex; flex-direction: column; gap: 6px; }
.im-btn-editor { display: flex; align-items: center; gap: 6px; }
.im-reply-btn-editor { flex-wrap: wrap; }
.im-row-actions {
  display: flex;
  gap: 10px;
  margin-top: 8px;
}
.im-add-link {
  font-size: 12px; color: #229ED9; border: none; background: transparent;
  cursor: pointer; padding: 0; font-weight: 500;
}
.im-add-link:hover { text-decoration: underline; }
.im-remove-link {
  font-size: 12px; color: #dc2626; border: none; background: transparent;
  cursor: pointer; padding: 0; font-weight: 500;
}
.im-remove-link:hover { text-decoration: underline; }
.im-add-row-btn {
  align-self: flex-start;
  font-size: 13px; color: #229ED9;
  border: 1px dashed #229ED9;
  border-radius: 8px; background: rgba(34,158,217,.06);
  padding: 7px 14px; cursor: pointer; font-weight: 500;
  transition: all .15s;
}
.im-add-row-btn:hover { background: rgba(34,158,217,.14); }

.im-checkbox-label {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: var(--text-secondary, #555);
  cursor: pointer;
  white-space: nowrap;
}
.im-radio { width: 16px; height: 16px; cursor: pointer; flex-shrink: 0; accent-color: #229ED9; }

.im-poll-options { display: flex; flex-direction: column; gap: 6px; margin-bottom: 4px; }
.im-poll-option-row { display: flex; align-items: center; gap: 6px; }
.im-opt-num { font-size: 12px; color: var(--text-secondary, #aaa); width: 18px; flex-shrink: 0; text-align: right; }
.im-poll-settings { display: flex; gap: 16px; flex-wrap: wrap; margin-top: 10px; }
.im-quiz-hint { font-size: 11px; color: var(--text-secondary, #aaa); font-style: italic; margin: 4px 0; }

/* Right column — preview */
.im-preview {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: #e8f4fd;
  overflow: hidden;
}
.im-preview-label {
  font-size: 11px;
  font-weight: 700;
  color: #229ED9;
  text-transform: uppercase;
  letter-spacing: .5px;
  padding: 14px 18px 8px;
}
.preview-frame {
  flex: 1;
  overflow-y: auto;
  padding: 10px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.preview-bubble {
  max-width: 85%;
  padding: 10px 14px;
  border-radius: 18px 18px 18px 4px;
  font-size: 13px;
  line-height: 1.5;
  word-break: break-word;
  box-shadow: 0 1px 3px rgba(0,0,0,.1);
}
.preview-bubble-bot {
  background: white;
  color: #1a1a2e;
  align-self: flex-start;
}

/* Inline buttons preview */
.preview-inline-grid { display: flex; flex-direction: column; gap: 5px; width: 100%; max-width: 85%; }
.preview-inline-row { display: flex; gap: 5px; }
.preview-inline-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 10px;
  background: white;
  border: 1.5px solid #229ED9;
  border-radius: 8px;
  color: #229ED9;
  font-size: 12px;
  font-weight: 500;
  cursor: default;
  box-shadow: 0 1px 3px rgba(0,0,0,.08);
  text-align: center;
}
.preview-btn-url { border-color: #7c3aed; color: #7c3aed; }

/* Reply keyboard preview */
.preview-reply-keyboard {
  background: rgba(0,0,0,.04);
  border-top: 1px solid rgba(0,0,0,.08);
  padding: 8px 6px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  border-radius: 0 0 12px 12px;
  margin-top: auto;
}
.preview-reply-row { display: flex; gap: 5px; }
.preview-reply-btn {
  flex: 1;
  padding: 9px 8px;
  background: white;
  border: 1px solid var(--border-primary, #e0e0e0);
  border-radius: 8px;
  font-size: 12px;
  text-align: center;
  cursor: default;
  box-shadow: 0 1px 2px rgba(0,0,0,.06);
}

/* Poll / quiz preview */
.preview-poll-card {
  background: white;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,.1);
  max-width: 88%;
  width: 100%;
}
.preview-quiz-card { border-top: 3px solid #7c3aed; }
.preview-poll-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 14px 6px;
}
.preview-poll-icon { font-size: 16px; }
.preview-poll-type { font-size: 11px; font-weight: 700; color: var(--text-secondary, #888); text-transform: uppercase; letter-spacing: .3px; }
.preview-poll-question { font-size: 14px; font-weight: 600; color: #1a1a2e; padding: 0 14px 10px; line-height: 1.4; }
.preview-poll-options { display: flex; flex-direction: column; gap: 1px; padding: 0 14px; }
.preview-poll-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 0;
  border-bottom: 1px solid #f0f0f0;
  font-size: 13px;
  color: #333;
  position: relative;
}
.preview-poll-option:last-child { border-bottom: none; }
.preview-poll-radio {
  width: 17px; height: 17px;
  border-radius: 50%;
  border: 2px solid #229ED9;
  flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: white;
}
.preview-radio-correct { background: #229ED9; border-color: #229ED9; }
.preview-opt-correct { font-weight: 600; color: #229ED9; }
.preview-correct-tag {
  margin-left: auto;
  font-size: 10px;
  font-weight: 700;
  color: #229ED9;
  background: rgba(34,158,217,.1);
  border-radius: 4px;
  padding: 1px 6px;
}
.preview-explanation {
  margin: 6px 14px 8px;
  padding: 8px 10px;
  background: rgba(34,158,217,.07);
  border-radius: 8px;
  font-size: 12px;
  color: #555;
  line-height: 1.4;
}
.preview-poll-footer {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-top: 1px solid #f0f0f0;
}
.preview-vote-btn {
  padding: 6px 18px;
  background: #229ED9;
  color: white;
  border: none;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  cursor: default;
}
.preview-vote-count { font-size: 12px; color: #aaa; }

/* Footer */
.im-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 22px;
  border-top: 1px solid var(--border-primary, #f0f0f0);
  flex-shrink: 0;
  background: var(--bg-primary, #fff);
}
.im-btn-cancel {
  padding: 9px 20px;
  border: 1px solid var(--border-primary, #ddd);
  border-radius: 8px;
  background: var(--bg-secondary, #f5f5f5);
  color: var(--text-secondary, #666);
  font-size: 13px;
  cursor: pointer;
  transition: all .15s;
}
.im-btn-cancel:hover { background: var(--bg-primary, white); border-color: #aaa; }
.im-btn-send {
  display: flex; align-items: center; gap: 7px;
  padding: 9px 22px;
  background: #229ED9;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background .2s;
}
.im-btn-send:hover:not(:disabled) { background: #1a8dbf; }
.im-btn-send:disabled { opacity: .55; cursor: not-allowed; }

/* ── Poll/quiz message bubble in conversation ─────────────────────────── */
.msg-poll {
  padding: 12px 14px;
  background: white;
  border-radius: 12px;
  min-width: 200px;
  max-width: 340px;
  box-shadow: 0 1px 4px rgba(0,0,0,.08);
}
.msg-poll-header {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 5px;
}
.msg-poll-icon { font-size: 14px; }
.msg-poll-type {
  font-size: 10px;
  font-weight: 700;
  color: #888;
  text-transform: uppercase;
  letter-spacing: .3px;
}
.msg-poll-question {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 8px;
  line-height: 1.4;
}
.msg-poll-options { display: flex; flex-direction: column; gap: 5px; }
.msg-poll-option {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #444;
}
.opt-radio { color: #229ED9; font-size: 14px; }
.opt-correct { color: #229ED9; font-size: 14px; font-weight: 700; }

/* ── Send bar ─────────────────────────────────────────────────────────── */
.send-bar {
  background: var(--bg-primary, white);
  border-top: 1px solid var(--border-primary, #e8e8e8);
  padding: 10px 14px;
  flex-shrink: 0;
}
.replying-to {
  display: flex; align-items: center; justify-content: space-between;
  background: rgba(34,158,217,.08);
  border-left: 3px solid #229ED9;
  padding: 6px 10px; border-radius: 4px;
  font-size: 12px; color: var(--text-secondary, #555); margin-bottom: 8px;
}
.reply-cancel {
  border: none; background: transparent; cursor: pointer;
  font-size: 16px; color: var(--text-secondary, #888); line-height: 1;
}
.send-row { display: flex; align-items: flex-end; gap: 8px; }
.attach-btn {
  width: 36px; height: 36px;
  border: 1px solid var(--border-primary, #e0e0e0);
  border-radius: 8px;
  background: var(--bg-primary, white);
  color: var(--text-secondary, #888);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; flex-shrink: 0; transition: all .15s;
}
.attach-btn:hover, .attach-btn.active { border-color: #229ED9; color: #229ED9; background: rgba(34,158,217,.08); }
.hidden-file { display: none; }
.send-input {
  flex: 1;
  border: 1px solid var(--border-primary, #e0e0e0);
  border-radius: 10px; padding: 8px 12px; font-size: 14px;
  resize: none; outline: none; font-family: inherit; line-height: 1.4;
  max-height: 100px; overflow-y: auto; transition: border-color .2s;
  background: var(--bg-secondary, #f8f8f8);
  color: var(--text-primary, #1a1a2e);
}
.send-input:focus { border-color: #229ED9; background: var(--bg-primary, white); }
.send-btn {
  width: 38px; height: 38px; border: none; border-radius: 50%;
  background: #229ED9; color: white;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; flex-shrink: 0; transition: background .2s;
}
.send-btn:hover:not(:disabled) { background: #1a8dbf; }
.send-btn:disabled { opacity: .5; cursor: not-allowed; }
.file-preview {
  display: flex; align-items: center; gap: 8px; margin-top: 6px;
  font-size: 12px; color: var(--text-secondary, #555);
  background: var(--bg-secondary, #f5f5f5); padding: 4px 10px; border-radius: 6px;
}
.file-thumb {
  width: 36px; height: 36px; object-fit: cover; border-radius: 4px; flex-shrink: 0;
}
.file-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.file-remove { border: none; background: transparent; cursor: pointer; color: #dc2626; font-size: 16px; }

/* ── Spinner ──────────────────────────────────────────────────────────── */
.spinner {
  width: 24px; height: 24px;
  border: 3px solid var(--border-primary, #e0e0e0);
  border-top-color: #229ED9;
  border-radius: 50%; animation: spin .7s linear infinite;
}
.spinner-sm {
  display: inline-block; width: 14px; height: 14px;
  border: 2px solid var(--border-primary, #e0e0e0); border-top-color: #229ED9;
  border-radius: 50%; animation: spin .7s linear infinite;
}
.load-more-row {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
}
/* ── Blocked banner ──────────────────────────────────────────────────── */
.blocked-banner {
  display: flex; align-items: center; gap: 6px;
  margin: 8px 12px; padding: 8px 12px;
  background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px;
  color: #dc2626; font-size: 12px;
}
:global(.dark) .blocked-banner { background: rgba(220,38,38,.12); border-color: rgba(220,38,38,.3); color: #f87171; }

/* ── Notes panel ──────────────────────────────────────────────────────── */
.notes-panel {
  border-bottom: 1px solid var(--border-primary, #eee);
  background: var(--bg-primary, #fff);
}
.notes-toggle {
  display: flex; align-items: center; gap: 6px;
  width: 100%; padding: 7px 16px; border: none; background: transparent;
  font-size: 12px; color: var(--text-secondary, #888); cursor: pointer;
  text-align: left;
}
.notes-toggle:hover { background: var(--bg-secondary, #f5f5f5); }
.notes-body { padding: 0 12px 10px; }
.notes-textarea {
  width: 100%; resize: vertical; min-height: 64px;
  border: 1px solid var(--border-primary, #ddd); border-radius: 6px;
  padding: 8px 10px; font-size: 13px; font-family: inherit;
  background: var(--bg-secondary, #f9f9f9); color: var(--text-primary, #333);
  box-sizing: border-box;
}
.notes-textarea:focus { outline: none; border-color: #229ED9; }
.notes-saving { font-size: 11px; color: var(--text-secondary, #aaa); margin-top: 4px; display: block; }

/* ── Quick replies ────────────────────────────────────────────────────── */
.quick-replies-wrapper { position: relative; }
.qr-badge {
  position: absolute; top: 2px; right: 2px;
  background: #229ED9; color: #fff; border-radius: 50%;
  font-size: 9px; width: 14px; height: 14px;
  display: flex; align-items: center; justify-content: center; font-weight: 700;
}
.qr-dropdown {
  position: absolute; bottom: calc(100% + 8px); left: 0;
  width: 300px; max-height: 340px; overflow-y: auto;
  background: var(--bg-primary, #fff);
  border: 1px solid var(--border-primary, #e0e0e0);
  border-radius: 10px; box-shadow: 0 4px 20px rgba(0,0,0,.12);
  z-index: 200;
}
.qr-loading { display: flex; justify-content: center; padding: 20px; }
.qr-empty { padding: 16px; font-size: 13px; color: var(--text-secondary, #888); text-align: center; }
.qr-create-link { display: block; margin-top: 6px; color: #229ED9; text-decoration: none; font-size: 12px; }
.qr-list { padding: 6px; }
.qr-item {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 10px; border-radius: 7px; cursor: pointer;
}
.qr-item:hover { background: var(--bg-secondary, #f5f5f5); }
.qr-thumb { width: 36px; height: 36px; object-fit: cover; border-radius: 5px; flex-shrink: 0; }
.qr-text { min-width: 0; }
.qr-title { font-size: 13px; font-weight: 600; color: var(--text-primary, #333); }
.qr-preview { font-size: 12px; color: var(--text-secondary, #888); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* ── Block action button ──────────────────────────────────────────────── */
.action-btn.action-blocked { color: #dc2626; background: #fef2f2; border-color: #fecaca; }
.action-btn.action-blocked:hover { background: #fee2e2; }

.load-more-end {
  font-size: 11px;
  color: var(--text-secondary, #aaa);
  letter-spacing: .3px;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Lightbox ─────────────────────────────────────────────────────────── */
.lightbox {
  position: fixed; inset: 0; background: rgba(0,0,0,.85);
  display: flex; align-items: center; justify-content: center;
  z-index: 2000; cursor: pointer;
}
.lightbox-img { max-width: 90vw; max-height: 90vh; border-radius: 8px; object-fit: contain; }

/* ── Dark mode overrides ──────────────────────────────────────────────── */
:global(.dark) .contact-item.active { background: rgba(34,158,217,.2); }
:global(.dark) .bubble-customer { background: var(--bg-tertiary); box-shadow: none; }
:global(.dark) .reply-context { background: rgba(255,255,255,.06); }
:global(.dark) .msg-document { background: rgba(255,255,255,.06); }
:global(.dark) .action-btn.action-resolved {
  color: #6ee7b7;
  background: rgba(6,95,70,.35);
  border-color: rgba(167,243,208,.3);
}
:global(.dark) .replying-to { background: rgba(34,158,217,.12); }
:global(.dark) .chat-header { box-shadow: 0 1px 3px rgba(0,0,0,.3); }

/* ── Mobile ───────────────────────────────────────────────────────────── */
@media (max-width: 768px) {
  .main-content { margin-left: 0; }
  .page-content { padding: 0; }
  .telegram-container { border-radius: 0; border: none; max-height: 100vh; }
  .inbox-panel { width: 100%; max-width: 100%; border-right: none; }
  .hidden-mobile { display: none !important; }
  .back-btn { display: flex !important; }
  .chat-header-actions { display: none; }
  .message-row { max-width: 85%; }
}

/* ── Message body row (holds actions + bubble side by side) ───────────── */
.msg-body {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: 4px;
}

/* ── Message hover action toolbar ─────────────────────────────────────── */
.msg-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  transition: opacity .18s ease;
  flex-shrink: 0;
}
.msg-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px; height: 28px;
  border-radius: 50%;
  border: none;
  background: var(--bg-primary, white);
  color: var(--text-secondary, #888);
  cursor: pointer;
  box-shadow: 0 1px 4px rgba(0,0,0,.14);
  transition: background .15s, color .15s;
  padding: 0;
}
.msg-action-btn:hover {
  background: #229ED9;
  color: white;
}

/* ── Polished reply banner ────────────────────────────────────────────── */
.replying-to {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: rgba(34,158,217,.08);
  border-radius: 10px 10px 0 0;
  border-left: 3px solid #229ED9;
  margin: 0 8px;
  animation: slide-down .15s ease;
}
@keyframes slide-down {
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
}
.replying-to-bar {
  width: 3px; height: 32px;
  background: #229ED9;
  border-radius: 2px;
  flex-shrink: 0;
  display: none; /* bar is already done via border-left */
}
.replying-to-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.replying-to-label {
  font-size: 11px;
  font-weight: 600;
  color: #229ED9;
  text-transform: uppercase;
  letter-spacing: .4px;
}
.replying-to-text {
  font-size: 13px;
  color: var(--text-primary, #333);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 480px;
}
.reply-cancel {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px; height: 26px;
  border: none;
  background: transparent;
  color: var(--text-secondary, #999);
  cursor: pointer;
  border-radius: 50%;
  padding: 0;
  transition: background .15s, color .15s;
  flex-shrink: 0;
}
.reply-cancel:hover { background: rgba(0,0,0,.08); color: var(--text-primary, #333); }

/* ── Forward modal ────────────────────────────────────────────────────── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.45);
  backdrop-filter: blur(3px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.forward-modal {
  background: var(--bg-primary, white);
  border-radius: 16px;
  width: 100%;
  max-width: 420px;
  max-height: 580px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0,0,0,.25);
  overflow: hidden;
  animation: modal-pop .18s ease;
}
@keyframes modal-pop {
  from { opacity: 0; transform: scale(.94); }
  to   { opacity: 1; transform: scale(1); }
}
.forward-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-primary, #f0f0f0);
}
.forward-modal-header h3 {
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary, #1a1a2e);
  margin: 0;
}
.fwd-close-btn {
  display: flex; align-items: center; justify-content: center;
  width: 28px; height: 28px;
  border: none; background: transparent;
  color: var(--text-secondary, #888);
  cursor: pointer; border-radius: 50%; padding: 0;
  transition: background .15s;
}
.fwd-close-btn:hover { background: var(--bg-secondary, #f5f5f5); }

.forward-preview {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: var(--bg-secondary, #f8f9fb);
  border-bottom: 1px solid var(--border-primary, #f0f0f0);
}
.forward-preview-icon {
  color: #229ED9;
  flex-shrink: 0;
  display: flex;
  align-items: center;
}
.forward-preview-text {
  font-size: 13px;
  color: var(--text-secondary, #666);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-style: italic;
}

.forward-search-box {
  position: relative;
  padding: 10px 14px;
  border-bottom: 1px solid var(--border-primary, #f0f0f0);
}
.forward-search-box svg {
  position: absolute;
  left: 26px; top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary, #aaa);
  pointer-events: none;
}
.forward-search-input {
  width: 100%;
  padding: 8px 10px 8px 30px;
  border: 1px solid var(--border-primary, #e8e8e8);
  border-radius: 8px;
  font-size: 13px;
  background: var(--bg-secondary, #f8f8f8);
  color: var(--text-primary, #1a1a2e);
  outline: none;
  box-sizing: border-box;
  transition: border-color .2s;
}
.forward-search-input:focus { border-color: #229ED9; background: var(--bg-primary, white); }

.forward-contacts-list {
  flex: 1;
  overflow-y: auto;
  min-height: 80px;
}
.fwd-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px;
  color: var(--text-secondary, #aaa);
  font-size: 13px;
}
.fwd-empty { flex-direction: column; }
.forward-contact-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 20px;
  cursor: pointer;
  transition: background .15s;
  border-bottom: 1px solid var(--border-primary, #f8f8f8);
}
.forward-contact-item:hover { background: rgba(34,158,217,.07); }
.fwd-avatar {
  width: 38px; height: 38px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; overflow: hidden;
}
.fwd-contact-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.fwd-contact-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary, #1a1a2e);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.fwd-contact-username {
  font-size: 12px;
  color: #229ED9;
}

/* ── Toast notification ───────────────────────────────────────────────── */
.tg-toast {
  position: fixed;
  bottom: 28px;
  left: 50%;
  transform: translateX(-50%);
  background: #1a1a2e;
  color: white;
  padding: 10px 20px;
  border-radius: 24px;
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 3000;
  box-shadow: 0 6px 24px rgba(0,0,0,.25);
  white-space: nowrap;
}
.tg-toast svg { color: #4ade80; flex-shrink: 0; }

.toast-slide-enter-active, .toast-slide-leave-active {
  transition: opacity .2s ease, transform .2s ease;
}
.toast-slide-enter-from, .toast-slide-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(12px);
}
</style>
